import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FullPageLoader } from './components/ui/Spinner';
import { AppLayout } from './components/AppLayout';

import Landing from './pages/Landing';
import Login from './pages/Login';
import AccessRequired from './pages/AccessRequired';
import Discover from './pages/Discover';
import Lessons from './pages/Lessons';
import LessonDetail from './pages/LessonDetail';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import Mentorship from './pages/Mentorship';
import MentorshipDetail from './pages/MentorshipDetail';
import TopicLessons from './pages/TopicLessons';
import Schedule from './pages/Schedule';
import Bookmarks from './pages/Bookmarks';
import Notifications from './pages/Notifications';
import Progress from './pages/Progress';
import Tools from './pages/Tools';
import Community from './pages/Community';
import Profile from './pages/Profile';
import Admin from './pages/Admin';

function MemberGate() {
  const { loading, user, access } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!access) return <AccessRequired />;
  return <AppLayout />;
}

// Route-level RBAC: only real admins may mount the Admin page. Runs after
// MemberGate (so user is loaded); non-admins are bounced to /app before the
// Admin component — and therefore any admin data fetch — ever renders.
function RequireAdmin({ children }) {
  const { loading, user } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-center" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/app" element={<MemberGate />}>
            <Route index element={<Discover />} />
            <Route path="lessons" element={<Lessons />} />
            <Route path="lessons/:lessonId" element={<LessonDetail />} />
            <Route path="courses" element={<Courses />} />
            <Route path="courses/:courseId" element={<CourseDetail />} />
            <Route path="mentorship" element={<Mentorship />} />
            <Route path="mentorship/:courseId" element={<MentorshipDetail />} />
            <Route path="topics/:topic" element={<TopicLessons />} />
            <Route path="schedule" element={<Schedule />} />
            <Route path="bookmarks" element={<Bookmarks />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="progress" element={<Progress />} />
            <Route path="tools" element={<Tools />} />
            <Route path="community" element={<Community />} />
            <Route path="profile" element={<Profile />} />
            <Route path="admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
